import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  FormGroup,
  Validators,
  FormBuilder,
  ReactiveFormsModule,
  FormsModule
} from '@angular/forms';
import { StorageService } from '../../core/storage/storage.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { UserRegister } from 'src/app/shared/model/register.model';
import { CoreService } from 'src/app/core/services/core.service';
import i18next from 'i18next';
import { ControlErrors } from 'src/app/shared/utils/generateError';
import { I18NextModule } from 'angular-i18next';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { Collective } from 'src/app/shared/model/user.model';
import { MatSelectModule } from '@angular/material/select';
import { ErrorWithGuideComponent } from 'src/app/shared/components/error-with-guide/error-with-guide.component';

@Component({
  standalone: true,
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  imports: [
    CommonModule,
    I18NextModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    ErrorWithGuideComponent
  ]
})
export class RegisterComponent implements OnInit, AfterViewInit {
  @ViewChild('autofocus') autofocus!: ElementRef<HTMLInputElement>;
  passwordsMatch = false;
  // CRMSP-7485 - Acceso a MyGarage VO para jubilados
  group = 'PSS';
  externVoRegisterType: 'FAMILY' | 'RETIREE' = 'FAMILY';
  userRegister: UserRegister = {
    nis: '',
    password: '',
    repeatPassword: ''
  };
  errorMessage = '';
  pattern = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[ -/:-@[-`{-~]).{12,60}$';
  loginErrorMessage: string | null = null;
  showDownloadGuideLink = false;

  registerForm: FormGroup = this.fb.group({
    nis: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.pattern(this.pattern)]],
    repeatPassword: [
      '',
      [Validators.required, Validators.pattern(this.pattern)]
    ],

    // CRMSP-7485 - Acceso a MyGarage VO para jubilados
    name: [''],
    surname: [''],
    birthDate: [''],
    email: [''],
    phone: ['']
  });

  passwordTranslate = i18next.t('passwordTranslate');
  spinner = false;
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private storageService: StorageService,
    private coreService: CoreService,
    private controlErrors: ControlErrors,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe((queryParams) => {
      if (queryParams['group']) {
        this.group = queryParams['group'];
      }

      if (this.group === 'RETIREE') {
        this.externVoRegisterType = 'RETIREE';
      } else if (this.group === 'EXTERNVO') {
        this.externVoRegisterType = 'FAMILY';
      }

      this.updateRetireeValidators();
    });
  }

  ngAfterViewInit() {
    this.autofocus.nativeElement.focus();
  }

  controlInput(input: string): boolean {
    return (
      this.registerForm?.controls[input]?.invalid &&
      this.registerForm?.controls[input]?.touched
    );
  }
  errorMsg(input: string) {
    this.spinner = false;
    if (
      this.registerForm?.controls[input]?.invalid &&
      this.registerForm?.controls[input]?.touched
    ) {
      return 'error-message';
    } else {
      return 'info-text';
    }
  }

  // CRMSP-7485 - Acceso a MyGarage VO para jubilados
  onSubmit() {
    this.spinner = true;
    this.errorMessage = '';
    this.loginErrorMessage = null;
    this.showDownloadGuideLink = false;

    // validar los campos del formulario
    this.userRegister = this.registerForm.value;

    if (this.userRegister.password !== this.userRegister.repeatPassword) {
      this.spinner = false;
      this.errorMessage = i18next.t('passwordErr');
      return;
    }

    this.passwordsMatch = true;

    // guardar la info del usuario en el storage
    this.storageService.set('userRegister', this.userRegister);

    if (this.group === 'RETIREE') {
      this.preRegisterRetiree();
    } else {
      this.registerCurrentFlow();
    }
  }

  private registerCurrentFlow(): void {
    this.coreService
      .registerUser(
        this.userRegister.nis,
        this.userRegister.password,
        undefined,
        Collective[this.group as keyof typeof Collective]
      )
      .subscribe({
        next: () => {
          this.spinner = false;
          this.router.navigate(['register/verification-code'], {
            queryParams: { group: this.group }
          });
        },
        error: (e: HttpErrorResponse) => {
          this.handleRegisterError(e);
        }
      });
  }

  private preRegisterRetiree(): void {
    this.coreService
      .preRegisterRetiree(
        this.registerForm.value.nis,
        this.registerForm.value.name,
        this.registerForm.value.surname,
        this.registerForm.value.birthDate,
        this.registerForm.value.email,
        this.registerForm.value.phone,
        Collective.RETIREE
      )
      .subscribe({
        next: () => {
          this.registerCurrentFlow();
        },
        error: (/*e: HttpErrorResponse*/) => {
          // this.handleRegisterError(e);
          this.errorMessage = i18next.t('genericErr');
          this.spinner = false;
        }
      });
  }

  private handleRegisterError(e: HttpErrorResponse): void {
    this.spinner = false;

    if (e.status === 406 && e.error.message === '5003') {
      this.errorMessage = this.controlErrors.generateError(
        e.error.message,
        i18next.t('errorNoPhones')
      );
    } else if (e.status === 406 && e.error.message === '5001') {
      this.errorMessage = this.controlErrors.generateError(
        e.error.message,
        i18next.t('re-verificationError')
      );
    } else if (e.status === 406 && e.error.message === '5005') {
      this.errorMessage = this.controlErrors.generateError(
        e.error.message,
        i18next.t('categoryError')
      );
    } else if (e.status === 412) {
      this.errorMessage = this.controlErrors.generateError(
        e.error.message,
        i18next.t('loginNoPss')
      );
    } else {
      this.errorMessage = this.controlErrors.generateError(
        e.error.message,
        i18next.t('genericErr')
      );
      this.loginErrorMessage = i18next.t('loginAdditionalMessage');
      this.showDownloadGuideLink = true;
    }
  }

  isEmail() {
    return this.group === 'FAMILY' || this.group === 'OTHERS';
  }

  // CRMSP-7485 - Acceso a MyGarage VO para jubilados
  changeExternVoRegisterType(value: 'FAMILY' | 'RETIREE'): void {
    this.externVoRegisterType = value;

    if (value === 'RETIREE') {
      this.group = 'RETIREE';
    } else {
      this.group = 'EXTERNVO';
    }

    this.updateRetireeValidators();
  }

  updateRetireeValidators(): void {
    const isRetiree = this.group === 'RETIREE';
    const requiresDniNie = this.isExternVoOrRetiree();

    const nis = this.registerForm.get('nis');
    const name = this.registerForm.get('name');
    const surname = this.registerForm.get('surname');
    const birthDate = this.registerForm.get('birthDate');
    const email = this.registerForm.get('email');
    const phone = this.registerForm.get('phone');

    nis?.setValidators(
      requiresDniNie
        ? [
          Validators.required,
          Validators.pattern(/^(?:\d{8}|[XYZ]\d{7})[A-Z]$/i)
        ]
        : [Validators.required]
    );

    if (isRetiree) {
      name?.setValidators([Validators.required]);
      surname?.setValidators([Validators.required]);
      birthDate?.setValidators([Validators.required]);
      email?.setValidators([Validators.required, Validators.email]);
      phone?.setValidators([
        Validators.required,
        Validators.pattern('^[0-9]{9}$')
      ]);
    } else {
      name?.clearValidators();
      surname?.clearValidators();
      birthDate?.clearValidators();
      email?.clearValidators();
      phone?.clearValidators();
    }
    nis?.updateValueAndValidity();
    name?.updateValueAndValidity();
    surname?.updateValueAndValidity();
    birthDate?.updateValueAndValidity();
    email?.updateValueAndValidity();
    phone?.updateValueAndValidity();
  }

  isExternVoOrRetiree(): boolean {
    return this.group === 'EXTERNVO' || this.group === 'RETIREE';
  }
}
